//
// Copyright 2024 DXOS.org
//

import { createCredential, createDidFromIdentityKey, signPresentation } from '@dxos/credentials';
import { type Signer } from '@dxos/crypto';
import { invariant } from '@dxos/invariant';
import { Keyring } from '@dxos/keyring';
import { IdentityDid, PublicKey } from '@dxos/keys';
import { type Chain, type Credential } from '@dxos/protocols/proto/dxos/halo/credentials';

import type { EdgeIdentity } from './edge-identity';

/**
 * Edge identity backed by a device key without a credential chain.
 */
export const createDeviceEdgeIdentity = async (signer: Signer, key: PublicKey): Promise<EdgeIdentity> => {
  return {
    identityDid: await createDidFromIdentityKey(key),
    peerKey: key.toHex(),
    presentCredentials: async ({ challenge }) => {
      return signPresentation({
        presentation: {
          credentials: [
            // Verifier requires at least one credential in the presentation to establish the subject.
            await createCredential({
              assertion: {
                '@type': 'dxos.halo.credentials.Auth',
              },
              issuer: key,
              subject: key,
              signer,
            }),
          ],
        },
        signer,
        signerKey: key,
        nonce: challenge,
      });
    },
  };
};

/**
 * Edge identity backed by a chain of credentials.
 */
export const createChainEdgeIdentity = async (
  signer: Signer,
  identityKey: PublicKey,
  peerKey: PublicKey,
  chain: Chain | undefined,
  credentials: Credential[],
): Promise<EdgeIdentity> => {
  const credentialsToSign =
    credentials.length > 0
      ? credentials
      : [
          await createCredential({
            assertion: {
              '@type': 'dxos.halo.credentials.Auth',
            },
            issuer: identityKey,
            subject: identityKey,
            signer,
            chain,
            signingKey: peerKey,
          }),
        ];

  return {
    identityDid: await createDidFromIdentityKey(identityKey),
    peerKey: peerKey.toHex(),
    presentCredentials: async ({ challenge }) => {
      // TODO: make chain required after device invitation flow update release
      invariant(chain);
      return signPresentation({
        presentation: {
          credentials: credentialsToSign,
        },
        signer,
        nonce: challenge,
        signerKey: peerKey,
        chain,
      });
    },
  };
};

/**
 * Edge identity backed by a random ephemeral key without HALO.
 */
export const createEphemeralEdgeIdentity = async (): Promise<EdgeIdentity> => {
  const keyring = new Keyring();
  const key = await keyring.createKey();
  return createDeviceEdgeIdentity(keyring, key);
};

/**
 * Creates a HALO chain of credentials to act as an edge identity.
 */
export const createTestHaloEdgeIdentity = async (
  signer: Signer,
  identityKey: PublicKey,
  deviceKey: PublicKey,
): Promise<EdgeIdentity> => {
  const deviceAdmission = await createCredential({
    assertion: {
      '@type': 'dxos.halo.credentials.AuthorizedDevice',
      deviceKey,
      identityKey,
    },
    issuer: identityKey,
    subject: deviceKey,
    signer,
  });
  return createChainEdgeIdentity(signer, identityKey, deviceKey, { credential: deviceAdmission }, [
    await createCredential({
      assertion: {
        '@type': 'dxos.halo.credentials.Auth',
      },
      issuer: identityKey,
      subject: identityKey,
      signer,
    }),
  ]);
};

export const createStubEdgeIdentity = (): EdgeIdentity => {
  const deviceKey = PublicKey.random();
  return {
    // Random placeholder DID — the stub never authenticates or connects; a real identity replaces it.
    identityDid: IdentityDid.random(),
    peerKey: deviceKey.toHex(),
    presentCredentials: async () => {
      throw new Error('Stub identity does not support authentication.');
    },
  };
};
